/**
 * One set type → the letter and the colour role it wears. The ONE table.
 *
 * It is shared because two surfaces read it and they must not drift: the builder's set table
 * (`features/plan-day-programming`), where a trainer AUTHORS a back-off, and the assigned-plan
 * calendar's session panel (`pages/assigned-plan-calendar`), where the same trainer reads back what
 * the athlete did with it. A trainer who writes a `T` and meets a `B0` has met two apps.
 *
 * ── The letters are this app's, not the KANON export's ─────────────────────────────────────────
 * The 5 August calendar export writes `B0` for a top set and `B` for a working one. This app already
 * ships `T` on the green wash and `W` on the neutral, transcribed from the RN app's
 * `design-system/setRoleColors.ts` (owner ask, 3 August 2026). Mobile wins; the deviation is
 * recorded in `pages/assigned-plan-calendar/AGENTS.md`.
 *
 * ── Case-insensitive, and here that is not laziness ────────────────────────────────────────────
 * The two endpoints disagree with each other about casing and have done since before this file:
 * `ExecutionSummarySlotSet.setType` is PascalCase (`"Working"`) where `DisplaySet.setType` is the
 * lower-cased enum name (`"working"`), which the backend's own DTO comment states outright. Both are
 * the same backend enum. Matching case-sensitively here would render every set of a finished session
 * as an unrecognised type. Contrast `sessionStatus.parseSessionStatus`, which IS case-sensitive on
 * purpose — there only one endpoint speaks, so a casing change would be news rather than a known
 * divergence.
 */

/**
 * The five roles the builder cycles, the inbound `Dropset` this build renders but never authors, and
 * `raw` — the fallback for a type a later backend adds that neither client knows yet.
 *
 * `dropset` is separate from `raw` on purpose. Both refuse to cycle, so they behave identically; they
 * do not LOOK identical, because a dropset is a set type this app understands and declines to write,
 * while a raw one is a set type it cannot read. Painting the two the same would tell the trainer
 * their plan holds something unrecognisable when it holds a drop set.
 */
export type SetTypeBadgeKind =
  | 'warmup'
  | 'working'
  | 'top'
  | 'backoff'
  | 'amrap'
  | 'dropset'
  | 'raw';

export interface SetTypeBadge {
  readonly letter: string;
  readonly kind: SetTypeBadgeKind;
}

/** Keyed on the LOWER-CASED wire value, which is what makes the two endpoints agree. */
const TABLE: Record<string, SetTypeBadge> = {
  warmup: { letter: 'R', kind: 'warmup' },
  working: { letter: 'W', kind: 'working' },
  top: { letter: 'T', kind: 'top' },
  backoff: { letter: 'B', kind: 'backoff' },
  amrap: { letter: 'A', kind: 'amrap' },
  dropset: { letter: 'D', kind: 'dropset' },
};

/**
 * The badge for one wire set type. A type neither client knows keeps its own initial and renders
 * neutral — silently mapping it onto `working` would tell the trainer a plan holds something it does
 * not.
 */
export function setTypeBadge(wire: string | null | undefined): SetTypeBadge {
  const known = TABLE[(wire ?? '').trim().toLowerCase()];
  if (known !== undefined) return known;
  const raw = (wire ?? '').trim();
  return { letter: (raw.charAt(0) || '?').toUpperCase(), kind: 'raw' };
}

/** Whether this type is prescribed WORK. Warm-up is the only set type that does not count. */
export function setTypeCountsAsWork(wire: string | null | undefined): boolean {
  return setTypeBadge(wire).kind !== 'warmup';
}
